import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Autocomplete,
  AutocompleteChangeReason,
  FormHelperText,
} from '@mui/material'
import {
  GroupRounded,
  PersonRounded,
  SearchOutlined,
  Close,
  BoltRounded,
  Description,
  Topic,
} from '@mui/icons-material'
import makeStyles from '@mui/styles/makeStyles'
import { matchSorter } from 'match-sorter'
import { observer } from 'mobx-react-lite'
import { ISelectorOption } from '@trivie/core'
import { color } from '@trivie/ui-web/theme/color'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { AutocompleteSelectorProps } from './autocomplete-selector.types'
import { useSelectorData } from './hooks/use-selector-data'
import { VirtualizedListbox } from './components/virtualized-listbox'
import { SelectorListboxContext } from './components/listbox-context'

const useStyles = makeStyles({
  paper: {
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out !important',
    borderRadius: '0px !important',
    boxShadow: 'none !important',
    border: '1px solid lightgray !important',
    borderBottomLeftRadius: '20px !important',
    borderBottomRightRadius: '20px !important',
    flex: 1,
    display: 'flex',
    borderTop: 'none !important',
    margin: '0px !important',
    width: '100% !important',
  },
  root: {
    '& .MuiAutocomplete-tag': {
      margin: '0 3px',
    },
  },
  popperDisablePortal: {
    transform: 'none !important',
    // @ts-ignore
    position: 'relative !important',
    width: '100% !important',
    flex: 1,
    display: 'flex',
    minHeight: '0px',
  },
  groupLabel: {
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  listbox: {
    minHeight: '0px !important',
    maxHeight: 'none !important',
    // No flex: 1 here — the wrapper div handles flex sizing, VariableSizeList uses explicit height
    transition: 'all 0.2s ease-in-out',
    '& ul': {
      padding: 0,
      margin: 0,
    },
    overscrollBehavior: 'none',
  },
  input: {
    '&::placeholder': {
      color: color.palette.darkGrey,
      fontSize: 14,
    },
    '& .MuiInputBase-root': {
      maxHeight: 80,
      minHeight: 60,
      overflow: 'auto',
      border: '1px solid lightgray',
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
    },
    '& .MuiInputBase-root::focused': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    '& .MuiInputBase-root > fieldset': {
      border: 'none',
    },
  },
})

export const getTypeIcon = (type: string, size: number = 12) => {
  if (type === 'Deactivated Users' || type === 'Active Users') {
    return false
  }
  switch (type) {
    case 'Groups':
      return <GroupRounded style={{ fontSize: size }} htmlColor={color.shade70} />
    case 'Users':
      return <PersonRounded style={{ fontSize: size }} htmlColor={color.shade70} />
    case 'topics':
      return <Topic style={{ fontSize: size }} htmlColor={color.shade70} />
    case 'quizzes':
      return <BoltRounded style={{ fontSize: size }} htmlColor={color.shade70} />
    case 'sources':
      return <Description style={{ fontSize: size }} htmlColor={color.shade70} />
    case 'learning paths':
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.7668 14.5C12.3113 14.5 11.9141 14.3639 11.5752 14.0917C11.2363 13.8194 11.0224 13.4889 10.9335 13.1H7.3835C6.65016 13.1 6.04183 12.8583 5.5585 12.375C5.07516 11.8917 4.8335 11.2833 4.8335 10.55C4.8335 9.81667 5.07516 9.20833 5.5585 8.725C6.04183 8.24167 6.65016 8 7.3835 8H8.66683C9.12239 8 9.49461 7.85556 9.7835 7.56667C10.0724 7.27778 10.2168 6.90556 10.2168 6.45C10.2168 5.99444 10.0724 5.62222 9.7835 5.33333C9.49461 5.04444 9.12239 4.9 8.66683 4.9H5.06683C4.96683 5.28889 4.75016 5.61944 4.41683 5.89167C4.0835 6.16389 3.68905 6.3 3.2335 6.3C2.70016 6.3 2.25016 6.11667 1.8835 5.75C1.51683 5.38333 1.3335 4.93333 1.3335 4.4C1.3335 3.86667 1.51683 3.41667 1.8835 3.05C2.25016 2.68333 2.70016 2.5 3.2335 2.5C3.68905 2.5 4.0835 2.63611 4.41683 2.90833C4.75016 3.18056 4.96683 3.51111 5.06683 3.9H8.66683C9.40016 3.9 10.0085 4.14167 10.4918 4.625C10.9752 5.10833 11.2168 5.71667 11.2168 6.45C11.2168 7.18333 10.9752 7.79167 10.4918 8.275C10.0085 8.75833 9.40016 9 8.66683 9H7.3835C6.92794 9 6.55572 9.14444 6.26683 9.43333C5.97794 9.72222 5.8335 10.0944 5.8335 10.55C5.8335 11.0056 5.97794 11.3778 6.26683 11.6667C6.55572 11.9556 6.92794 12.1 7.3835 12.1H10.9335C11.0335 11.7111 11.2502 11.3806 11.5835 11.1083C11.9168 10.8361 12.3113 10.7 12.7668 10.7C13.3002 10.7 13.7502 10.8833 14.1168 11.25C14.4835 11.6167 14.6668 12.0667 14.6668 12.6C14.6668 13.1333 14.4835 13.5833 14.1168 13.95C13.7502 14.3167 13.3002 14.5 12.7668 14.5ZM3.2335 5.3C3.48905 5.3 3.70294 5.21389 3.87516 5.04167C4.04739 4.86944 4.1335 4.65556 4.1335 4.4C4.1335 4.14444 4.04739 3.93056 3.87516 3.75833C3.70294 3.58611 3.48905 3.5 3.2335 3.5C2.97794 3.5 2.76405 3.58611 2.59183 3.75833C2.41961 3.93056 2.3335 4.14444 2.3335 4.4C2.3335 4.65556 2.41961 4.86944 2.59183 5.04167C2.76405 5.21389 2.97794 5.3 3.2335 5.3Z"
            fill="black"
            fillOpacity="0.7"
          />
        </svg>
      )
    default:
      return <Description style={{ fontSize: size }} htmlColor={color.shade70} />
  }
}

export const AutocompleteSelector = observer((props: AutocompleteSelectorProps) => {
  const {
    variant,
    options: externalOptions,
    onSelectionChange,
    preselectIds,
    defaultOptions,
    filterExcludeIds,
    filterExcludeTypes,
    open = true,
    placeholder = 'Search',
    noOptionsText = 'No options',
    showAvatar = true,
    style = {},
    helperText,
    error = false,
    maxSelections,
  } = props

  const { options: fetchedOptions, loading } = useSelectorData(variant, externalOptions)

  const [selectedOptions, setSelectedOptions] = useState<ISelectorOption[]>([])
  const [focused, setFocused] = useState(open)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)
  const preselectedRef = useRef(false)
  const defaultsAppliedRef = useRef(false)
  const selectedOptionsRef = useRef<ISelectorOption[]>([])
  selectedOptionsRef.current = selectedOptions

  const filteredOptions = useMemo(() => {
    let filtered = fetchedOptions
    if (filterExcludeIds?.length) {
      const isComposite = typeof filterExcludeIds[0] === 'object'
      if (isComposite) {
        const composite = filterExcludeIds as { id: number; type: string }[]
        filtered = filtered.filter(
          (o) =>
            !composite.some((c) => c.id === o.id && c.type.toLowerCase() === o.type.toLowerCase()),
        )
      } else {
        const ids = filterExcludeIds as number[]
        filtered = filtered.filter((o) => !ids.includes(o.id))
      }
    }
    if (filterExcludeTypes?.length) {
      filtered = filtered.filter((o) => !filterExcludeTypes.includes(o.type))
    }
    return filtered
  }, [fetchedOptions, filterExcludeIds, filterExcludeTypes])

  // Auto-enable collapsible groups when multiple groups are present
  const hasMultipleGroups = useMemo(() => {
    const types = new Set(filteredOptions.map((o) => o.type))
    return types.size > 1
  }, [filteredOptions])

  // Handle preselection for edit flows
  useEffect(() => {
    if (
      preselectIds &&
      preselectIds.length > 0 &&
      filteredOptions.length > 0 &&
      !preselectedRef.current &&
      !defaultsAppliedRef.current
    ) {
      preselectedRef.current = true
      const preselected = filteredOptions.filter((opt) => {
        return preselectIds.some(
          (p) => p.id === opt.id && p.type.toLowerCase() === opt.type.toLowerCase(),
        )
      })
      if (preselected.length > 0) {
        setSelectedOptions(preselected)
        onSelectionChange(preselected)
      }
    }
  }, [preselectIds, filteredOptions])

  // Handle defaultOptions (restore selections on remount)
  useEffect(() => {
    if (defaultOptions && defaultOptions.length > 0 && !defaultsAppliedRef.current) {
      defaultsAppliedRef.current = true
      setSelectedOptions(defaultOptions)
      onSelectionChange(defaultOptions)
    }
  }, [defaultOptions])

  const classes = useStyles()
  const inputBorderRadius = open || focused ? 0 : 20

  const onToggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const onToggleGroupCollapse = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  const autoSelectSubItems = (parentId: number): ISelectorOption[] => {
    return filteredOptions.filter((f) => f.parentId === parentId)
  }

  const handleChange = (
    _event: React.SyntheticEvent,
    value: ISelectorOption[],
    reason: AutocompleteChangeReason,
  ) => {
    let finalValue = value
    if (reason === 'selectOption') {
      const addedItem = value[value.length - 1]
      if (addedItem.subItems && addedItem.subItems.length > 0) {
        const subItems = autoSelectSubItems(addedItem.id)
        finalValue = [...value, ...subItems]
      }
    }

    if (reason === 'removeOption') {
      const removedItem = selectedOptions.find(
        (f) => !value.some((v) => v.id === f.id && v.type === f.type),
      )
      if (removedItem?.subItems && removedItem.subItems.length > 0) {
        finalValue = value.filter((f) => f.parentId !== removedItem.id)
      }
      setSelectedOptions(finalValue)
      onSelectionChange(finalValue, removedItem)
      return
    }

    setSelectedOptions(finalValue)
    onSelectionChange(finalValue)
  }

  const filterOptions = (o: ISelectorOption[], { inputValue }: { inputValue: string }) => {
    const matched = matchSorter(o, inputValue, { keys: ['*.label', '*.subLabel'] })
    return matched.sort((a, b) => (GROUP_ORDER[a.type] ?? 99) - (GROUP_ORDER[b.type] ?? 99))
  }

  const renderTags = (value: ISelectorOption[], getTagProps: any) => {
    return value.map((option: ISelectorOption, index: number) => (
      <Chip
        deleteIcon={<Close />}
        style={CHIP}
        avatar={
          getTypeIcon(option.type) ? (
            <div style={GROUP_ICON_MINI}>{getTypeIcon(option.type)}</div>
          ) : showAvatar ? (
            <Avatar
              style={{ left: 4, position: 'relative' }}
              fontStyle={{ fontSize: 8 }}
              size={21}
              name={option.subLabel ? option.label : ''}
              url={option.photo_url}
            />
          ) : (
            <div style={{ ...GROUP_ICON_MINI, width: 40, height: 40 }}>
              <PersonRounded style={{ fontSize: 12 }} htmlColor={color.shade70} />
            </div>
          )
        }
        variant="outlined"
        label={option.label}
        {...getTagProps({ index })}
      />
    ))
  }

  // Context value for the virtualized listbox
  const listboxContextValue = useMemo(
    () => ({
      showAvatar,
      selectedOptionsRef,
      expandedIds,
      onToggleExpand,
      collapsibleGroups: hasMultipleGroups,
      collapsedGroups,
      onToggleGroupCollapse,
    }),
    [showAvatar, expandedIds, hasMultipleGroups, collapsedGroups],
  )

  if (loading) {
    return (
      <div
        style={{
          ...style,
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </div>
    )
  }

  return (
    <SelectorListboxContext.Provider value={listboxContextValue}>
      <div
        ref={containerRef}
        style={{ ...style, display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}
      >
        <Autocomplete
          fullWidth
          disablePortal
          noOptionsText={noOptionsText}
          filterOptions={filterOptions}
          isOptionEqualToValue={(o: ISelectorOption, value: ISelectorOption) =>
            selectedOptions.some(() => value.type === o.type && value.id === o.id)
          }
          open={open}
          value={selectedOptions}
          onChange={handleChange}
          disableCloseOnSelect
          disableListWrap
          multiple
          id="autocomplete-selector"
          classes={{ ...classes }}
          forcePopupIcon={false}
          options={filteredOptions}
          groupBy={(option) => option.type}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={
            maxSelections != null && selectedOptions.length >= maxSelections
              ? (option) =>
                  !selectedOptions.some((s) => s.id === option.id && s.type === option.type)
              : undefined
          }
          renderTags={renderTags}
          renderInput={(params: any) => {
            params.InputProps.startAdornment = (
              <>
                <InputAdornment position="start">
                  <SearchOutlined htmlColor={color.shade30} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            )
            params.InputProps.endAdornment = null
            return (
              <TextField
                className={classes.input}
                placeholder={placeholder}
                {...params}
                variant="outlined"
                type="text"
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                sx={{
                  '& .MuiInputBase-root': {
                    borderBottomLeftRadius: inputBorderRadius,
                    borderBottomRightRadius: inputBorderRadius,
                  },
                }}
              />
            )
          }}
          renderOption={(optionProps, option, state) =>
            [optionProps, option, state.index] as React.ReactNode
          }
          renderGroup={(params) => params as any}
          ListboxComponent={VirtualizedListbox}
        />
        {helperText != null && (
          <FormHelperText error={error} sx={{ mx: '14px', mt: '6px' }}>
            {helperText}
          </FormHelperText>
        )}
      </div>
    </SelectorListboxContext.Provider>
  )
})

const GROUP_ORDER: Record<string, number> = {
  Groups: 0,
  Users: 1,
  'learning paths': 0,
  topics: 1,
  quizzes: 2,
  sources: 3,
}

const GROUP_ICON_MINI: React.CSSProperties = {
  height: 21,
  width: 21,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  borderRadius: 6,
  alignItems: 'center',
}
const CHIP: React.CSSProperties = {
  backgroundColor: '#EFF0F6',
  marginTop: 4,
  marginBottom: 4,
  border: 'none',
  borderRadius: 6,
}
